import React from 'react';
import { useQuery } from '@apollo/client';
import { Alert, AlertTitle, Box, Button, Link, Typography } from '@mui/material';
import type { ContractTemplateAssignment } from 'global-intern-types';
import { PagePathname } from '../../../routes/paths';
import { GET_ASSIGNMENT_CONFLICTS } from '../api/contractTemplates';
import { pruefeZuordnung, VorlagenZuordnungForm, zuordnungZumInput } from '../services/vorlagenZuordnung';

/** RITA-1572: Ohne „alle Vertragsarten“ meldet der Server nur noch gleiche Zuordnungen (`duplicate`). */
interface Konflikt {
  reason: 'duplicate';
  template: { id: number; name: string; assignment: ContractTemplateAssignment };
}
interface Props {
  wert: VorlagenZuordnungForm;
  vorlagenId: number | null;
  kopie?: boolean;
}

export default function VorlagenZuordnungHinweis({ wert, vorlagenId, kopie }: Props) {
  const vollstaendig = Object.keys(pruefeZuordnung(wert)).length === 0;
  const { data, loading, error, refetch } = useQuery<{ getContractTemplateAssignmentConflicts: Konflikt[] }>(
    GET_ASSIGNMENT_CONFLICTS,
    {
      variables: { assignment: zuordnungZumInput(wert), excludeId: vorlagenId },
      skip: !vollstaendig,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );
  if (!vollstaendig) return null;
  if (loading)
    return (
      <Typography role="status" variant="body2" sx={{ mt: 1 }}>
        Zuordnung wird geprüft …
      </Typography>
    );
  if (error || !data?.getContractTemplateAssignmentConflicts)
    return (
      <Alert
        severity="warning"
        sx={{ mt: 1 }}
        action={
          <Button
            color="inherit"
            onClick={() => {
              refetch().catch(() => undefined);
            }}
          >
            Erneut versuchen
          </Button>
        }
      >
        Die Zuordnung kann gerade nicht vorab geprüft werden. Beim Speichern wird sie erneut geprüft.
      </Alert>
    );
  return (
    <Box>
      {data?.getContractTemplateAssignmentConflicts.map(({ template }) => (
        <Alert key={template.id} severity="warning" sx={{ mt: 1 }}>
          <AlertTitle>Diese Zuordnung ist bereits vergeben.</AlertTitle>
          {`„${template.name}“ verwendet dieselbe Zuordnung. Bitte ändere die Zuordnung, bevor du ${kopie ? 'die Kopie' : 'die Vorlage'} speicherst.`}
          <Box sx={{ mt: 1 }}>
            <Link
              color="inherit"
              underline="always"
              href={PagePathname.vertragsvorlagenBearbeiten.replace(':id', String(template.id))}
              target="_blank"
              rel="noopener noreferrer"
            >
              Vorlage öffnen (neuer Tab)
            </Link>
          </Box>
        </Alert>
      ))}
    </Box>
  );
}
